import { z } from 'zod';
import { SLUG_PATTERN, RESERVED_SLUGS } from './slug';

/** Schemas de entrada compartilhados entre API (validação definitiva) e interface (feedback imediato). */

export const emailSchema = z.string().trim().toLowerCase().pipe(z.email({ message: 'E-mail inválido' })).pipe(z.string().max(254));

/** Senhas muito usadas/vazadas (amostra). Complementa o tamanho mínimo, conforme recomendação do OWASP ASVS (V2.1.7). */
const COMMON_PASSWORDS = new Set([
  '1234567890', '12345678910', 'qwerty1234', 'qwertyuiop1', 'senha12345', 'senha123456', 'password123', 'password1234', 'abc1234567', 'abcd123456',
  'admin12345', 'admin123456', 'restaurante1', 'restaurante123', 'garlic12345', 'mudar12345', 'trocar12345', 'iloveyou12', 'brasil1234', 'flamengo123',
  'corinthians1', 'palmeiras123', 'a1b2c3d4e5', '1q2w3e4r5t', '1qaz2wsx3edc', 'q1w2e3r4t5', 'zaq12wsx34', 'mesa123456', 'cozinha123', 'garcom12345',
]);

export const passwordSchema = z
  .string()
  .min(10, 'A senha precisa ter pelo menos 10 caracteres')
  .max(200, 'Senha longa demais')
  .refine((p) => /[a-zA-Z]/.test(p) && /\d/.test(p), 'Use letras e números')
  .refine((p) => !COMMON_PASSWORDS.has(p.toLowerCase()) && !/^(.)\1+$/.test(p.replace(/\d/g, '')), 'Senha muito comum. Escolha outra.');

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(SLUG_PATTERN, 'Use de 3 a 40 caracteres: letras minúsculas, números e hífen')
  .refine((s) => !s.includes('--'), 'Não use dois hífens seguidos')
  .refine((s) => !RESERVED_SLUGS.has(s), 'Este endereço é reservado pelo sistema');

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Cor inválida (use #RRGGBB)');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const phoneSchema = z
  .string()
  .trim()
  .regex(/^[\d\s()+-]{8,20}$/, 'Telefone inválido');

export const timezoneSchema = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat('pt-BR', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, 'Fuso horário inválido');

export const RESTAURANT_STATUSES = ['ONBOARDING', 'ACTIVE', 'SUSPENDED'] as const;
export type RestaurantStatus = (typeof RESTAURANT_STATUSES)[number];
export const RESTAURANT_STATUS_LABEL: Record<RestaurantStatus, string> = {
  ONBOARDING: 'Em implantação',
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
};

export const googleReviewUrlSchema = z
  .url({ message: 'URL inválida' })
  .refine((u) => {
    const host = new URL(u).hostname;
    return u.startsWith('https://') && (host === 'g.page' || host.endsWith('.google.com') || host === 'google.com' || host.endsWith('.goo.gl') || host === 'goo.gl' || host === 'maps.app.goo.gl');
  }, 'Use o link oficial de avaliação do Google (https://g.page/... ou https://search.google.com/...)');

export const createRestaurantSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome').max(120),
  slug: slugSchema,
  adminEmail: emailSchema,
  adminName: z.string().trim().min(2, 'Informe o nome do responsável').max(120),
  phone: phoneSchema,
  legalName: optionalText(160),
  document: optionalText(20),
  timezone: timezoneSchema.default('America/Sao_Paulo'),
  city: optionalText(80),
  state: optionalText(2),
});
export type CreateRestaurantInput = z.infer<typeof createRestaurantSchema>;

export const updateRestaurantSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  legalName: optionalText(160),
  document: optionalText(20),
  phone: phoneSchema.optional(),
  contactEmail: emailSchema.nullable().optional(),
  addressLine: optionalText(200),
  city: optionalText(80),
  state: optionalText(2),
  postalCode: optionalText(10),
  timezone: timezoneSchema.optional(),
  primaryColor: hexColor.optional(),
  accentColor: hexColor.optional(),
  googleReviewUrl: googleReviewUrlSchema.nullable().optional().or(z.literal('').transform(() => null)),
});
export type UpdateRestaurantInput = z.infer<typeof updateRestaurantSchema>;

export const changeStatusSchema = z.object({
  status: z.enum(RESTAURANT_STATUSES),
  reason: z.string().trim().min(5, 'Descreva o motivo (mínimo 5 caracteres)').max(500),
});

export const changeSlugSchema = z.object({
  slug: slugSchema,
  reason: z.string().trim().min(5, 'Descreva o motivo').max(500),
});

export const PLAN_FEATURES = ['online_payments', 'printing', 'reports_export', 'multi_station'] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];
export const PLAN_FEATURE_LABEL: Record<PlanFeature, string> = {
  online_payments: 'Pagamentos online (Mercado Pago)',
  printing: 'Impressão automática',
  reports_export: 'Exportação de relatórios',
  multi_station: 'Setores de produção adicionais',
};

export const updateSubscriptionSchema = z.object({
  plan: z.string().trim().min(2).max(40),
  maxTables: z.number().int().min(1).max(1000),
  maxStaff: z.number().int().min(1).max(1000),
  features: z.array(z.enum(PLAN_FEATURES)),
  monthlyFeeCents: z.number().int().min(0).max(100_000_00),
  notes: optionalText(1000),
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});

export const totpCodeSchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/, 'Código de 6 dígitos'),
});

export const requestResetSchema = z.object({ email: emailSchema });
export const completeResetSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});
